/*global WebView*/
/**
 * Contextual help panel (spec docs/superpowers/specs/2026-10-09-aide-contextuelle-design.md § 5)
 *
 * A singleton : a fixed side panel mounted on document.body, outside the main template (which
 * is re-rendered often) and NOT a route (a popup route would close on every navigation and
 * would become the "current screen" itself). It follows the screen through the router
 * "routeChanged" event and reads the help files published in help/<lang>/ by
 * `npm run doc:help`, lazily : nothing is downloaded before the first opening.
 *
 * Modes (data-help-state on the panel root) : loading | empty | unavailable | screen | read |
 * toc | search | ask.
 *
 * "ask" (roadmap lot L3.5) : a question to the documentation assistant (api/docAssistant/ask),
 * answered with citations that open the cited section in the panel. Its form is a static block
 * of the template, so the conversation survives reading a citation and coming back, and closing
 * and reopening the panel (not a reload of the page). The mode exists only when
 * api/docAssistant/status says the usage is on.
 *
 * Conversation (spec 2026-10-20-assistant-doc-conversation) : a short thread of at most
 * MAX_TURNS exchanges ; the client keeps it and sends the previous exchanges back as `history`
 * with each question (the server keeps nothing).
 *
 * Beta : the whole panel is reserved to the shared OCI admin account (`adm`, see isAllowed) :
 * the top-bar button is shown only to it (main.html, `view.user.isAdm`), F1 and open() do
 * nothing for any other login, and the server refuses the assistant to them.
 */
const {helpLang, screenHelp, searchTitles, parseRef} = require("./helpIndex") ;
const {escapeHtml, screenHtml, readHtml, tocHtml, searchHtml} = require("./helpRender") ;
const {resolveScreens} = require("./helpIndex") ;
const helpHighlight = require("./helpHighlight") ;
const helpTour = require("./helpTour") ;
const {threadHtml} = require("./helpAsk") ;

const STORAGE_KEY = "owork.helpPanel" ;
const HISTORY_MAX = 30 ;
const ROUTE_DEBOUNCE = 150 ;
const SEARCH_DEBOUNCE = 150 ;
const SEARCH_LIMIT = 20 ;
const MAX_TURNS = 6 ;//exchanges in one conversation, unless the server says otherwise (maxTurns)

const helpPanel = {
    appController: null,
    host: null,
    view: null,
    viewReady: null,
    opened: false,
    lang: null,
    indexes: new Map(), //lang → Promise(index)
    pages: new Map(), //lang/file → Promise(page data)
    current: null, //what is displayed : {mode: "screen", code?} | {mode: "read", ref} | {mode: "toc"} | {mode: "search", query}
    history: [],
    renderSeq: 0,
} ;

function tr(key){
    return WebView.tr(key) ;
}

function rememberOpen(open){
    try{
        if(open){ window.sessionStorage.setItem(STORAGE_KEY, "open") ; }
        else{ window.sessionStorage.removeItem(STORAGE_KEY) ; }
    }catch(err){
        //private mode / storage disabled : the panel just won't reopen after a reload
    }
}

function wasOpen(){
    try{
        return window.sessionStorage.getItem(STORAGE_KEY) === "open" ;
    }catch(err){
        return false ;
    }
}

function debounce(fn, delay){
    let timer = null ;
    return function(){
        const args = arguments ;
        clearTimeout(timer) ;
        timer = setTimeout(() => fn.apply(null, args), delay) ;
    } ;
}

function fetchJson(url, options){
    return fetch(url, options).then((response) => {
        if(!response.ok){ throw new Error("HTTP "+response.status+" "+url) ; }
        return response.json() ;
    }) ;
}

/**
 * Help language, read again at each use : the user may switch language in his preferences
 */
helpPanel.currentLang = function(){
    let lang = "fr" ;
    try{
        lang = WebView.i18n.getLang() ;
    }catch(err){
        //i18n not configured yet : French, the default language of the application
    }
    return helpLang(lang) ;
} ;

helpPanel.loadIndex = function(lang){
    if(!this.indexes.has(lang)){
        //no-cache = revalidate (ETag) : always the help of the deployed version
        const promise = fetchJson("help/"+lang+"/index.json", {cache: "no-cache"}) ;
        promise.catch(() => this.indexes.delete(lang)) ;//retry on next opening
        this.indexes.set(lang, promise) ;
    }
    return this.indexes.get(lang) ;
} ;

helpPanel.loadPage = function(lang, index, pageIdx){
    const file = index.pages[pageIdx].file ;
    const key = lang+"/"+file ;
    if(!this.pages.has(key)){
        //file names are content hashes : immutable, the browser cache is safe
        const promise = fetchJson("help/"+lang+"/"+file) ;
        promise.catch(() => this.pages.delete(key)) ;
        this.pages.set(key, promise) ;
    }
    return this.pages.get(key) ;
} ;

/**
 * Wire the panel to the router. Idempotent.
 *
 * @param {object} appController the application router (window.app)
 */
helpPanel.init = function(appController){
    if(this.appController){ return ; }
    this.appController = appController ;
    if(appController.context){
        //E2E tests and the future chatbot citations open a section with help.open({ref})
        appController.context.help = this ;
    }

    document.addEventListener("keydown", (ev) => {
        if((ev.key === "F1" || ev.keyCode === 112) && this.isAllowed()){
            ev.preventDefault() ;//otherwise Chrome opens its own help page
            this.toggle() ;
        }
    }, true) ;

    //reopened after a reload of the tab, once the first screen is displayed : before that
    //the user language may not be applied yet (i18n falls back on the browser language)
    let reopen = wasOpen() ;
    appController.on("routeChanged", debounce(() => {
        if(reopen){
            reopen = false ;
            if(!this.opened){ return this.open() ; }
        }
        this.onRouteChanged() ;
    }, ROUTE_DEBOUNCE)) ;
} ;

/**
 * Beta : only the adm account gets the panel (same rule as user.isAdm on the server,
 * admStepUp.isAdm : the login, trimmed, case-insensitive)
 */
helpPanel.isAllowed = function(){
    const context = this.appController && this.appController.context ;
    const user = context && context.api && context.api.currentUser ;
    return !!(user && (user.isAdm || String(user.login || "").trim().toLowerCase() === "adm")) ;
} ;

helpPanel.isOpen = function(){
    return this.opened ;
} ;

helpPanel.toggle = function(){
    if(this.opened){ this.close() ; }
    else{ this.open() ; }
} ;

/**
 * Open the panel
 *
 * @param {object} [options] {ref: "page#anchor"} to read a section, {toc: true} for the full
 *  documentation, {ask: true} to ask a question, nothing for the help of the current screen
 * @return {Promise} resolved once the content is displayed
 */
helpPanel.open = async function(options){
    if(!this.isAllowed()){ return ; }
    const opts = options || {} ;
    let target = {mode: "screen"} ;
    if(opts.ref){ target = {mode: "read", ref: opts.ref} ; }
    else if(opts.toc){ target = {mode: "toc"} ; }
    else if(opts.ask){ target = {mode: "ask"} ; }

    await this.mount() ;
    this.loadAskStatus() ;
    const wasOpened = this.opened ;
    this.opened = true ;
    this.placeBelowTopBar() ;
    this.host.classList.add("help-panel-open") ;
    this.dock(true) ;
    this.host.setAttribute("aria-hidden", "false") ;
    rememberOpen(true) ;
    this.markToggle() ;
    if(!wasOpened){
        this.history = [] ;
        this.current = null ;
        this.searchInput().value = "" ;
        this.searchInput().focus() ;
    }
    await this.show(target, wasOpened) ;
} ;

helpPanel.close = function(){
    if(!this.opened){ return ; }
    this.opened = false ;
    this.renderSeq++ ;//drop any pending rendering
    this.host.classList.remove("help-panel-open") ;
    this.dock(false) ;
    helpTour.end() ;
    helpHighlight.clear() ;
    this.host.setAttribute("aria-hidden", "true") ;
    rememberOpen(false) ;
    this.markToggle() ;
    const focusInPanel = this.host.contains(document.activeElement) ;
    const toggle = this.visibleToggle() ;
    if(focusInPanel && toggle){ toggle.focus() ; }
} ;

/**
 * Create the panel DOM on first use
 */
helpPanel.mount = function(){
    if(!this.viewReady){
        this.host = document.createElement("div") ;
        this.host.className = "help-panel-host" ;
        this.host.setAttribute("data-help-panel", "") ;
        this.host.setAttribute("role", "complementary") ;
        this.host.setAttribute("aria-hidden", "true") ;
        this.host.setAttribute("data-help-state", "loading") ;
        document.body.appendChild(this.host) ;
        this.view = new WebView("views/help", "helpPanel", {container: this.host}) ;
        this.viewReady = this.view.open({}).then(() => this.bindEvents()) ;
    }
    return this.viewReady ;
} ;

/**
 * Start the panel under the top bar (V1 and V2 : nav.main-menu-bar) : full height, it covered
 * the help button itself, the notifications and the user menu (L1.5 review)
 */
helpPanel.placeBelowTopBar = function(){
    const bar = document.querySelector(".main-menu-bar") ;
    const bottom = bar && bar.offsetParent !== null ? Math.round(bar.getBoundingClientRect().bottom) : 0 ;
    this.host.style.top = Math.max(0, bottom)+"px" ;
} ;

helpPanel.searchInput = function(){
    return this.host.querySelector("[data-help-search]") ;
} ;

helpPanel.contentEl = function(){
    return this.host.querySelector("[data-help-content]") ;
} ;

helpPanel.visibleToggle = function(){
    const toggles = document.querySelectorAll("[data-help-toggle]") ;
    for(let i=0; i<toggles.length; i++){
        if(toggles[i].offsetParent !== null){ return toggles[i] ; }
    }
    return null ;
} ;

/**
 * Active state of the top bar button, set on the DOM (the main template is re-rendered often,
 * so it is set again after each route change)
 */
helpPanel.markToggle = function(){
    const toggles = document.querySelectorAll("[data-help-toggle]") ;
    for(let i=0; i<toggles.length; i++){
        toggles[i].classList.toggle("active", this.opened) ;
        toggles[i].setAttribute("aria-expanded", this.opened ? "true" : "false") ;
        //filled icon while open : the state shows by shape, not only by color (L1.5b)
        const icon = toggles[i].querySelector(".fa-question-circle") ;
        if(icon){
            icon.classList.toggle("fas", this.opened) ;
            icon.classList.toggle("far", !this.opened) ;
        }
    }
} ;

helpPanel.bindEvents = function(){
    this.host.addEventListener("click", (ev) => this.onClick(ev)) ;
    this.host.addEventListener("keydown", (ev) => {
        if(ev.key !== "Escape"){ return ; }
        const input = this.searchInput() ;
        if(ev.target === input && input.value){
            input.value = "" ;
            this.onSearch("") ;
        }else{
            this.close() ;
        }
        ev.stopPropagation() ;
    }) ;
    const onSearch = debounce((value) => this.onSearch(value), SEARCH_DEBOUNCE) ;
    this.searchInput().addEventListener("input", (ev) => onSearch(ev.target.value)) ;
    this.bindAsk() ;
    helpHighlight.attach(this) ;
    helpTour.attach(this) ;
} ;

/**
 * Wide screens : the application makes room for the panel instead of being covered by it, so
 * that a highlighted zone is never under the panel (L1.7d). The margin itself is a media query
 * of helpPanel.scss on html.help-panel-docked (no docking when the screen is too narrow) ; the
 * resize event lets the grids and the layout take the new width.
 */
helpPanel.dock = function(on){
    const root = document.documentElement ;
    if(root.classList.contains("help-panel-docked") === on){ return ; }
    root.classList.toggle("help-panel-docked", on) ;
    try{
        window.dispatchEvent(new Event("resize")) ;
    }catch(err){
        //very old browser without the Event constructor : the layout catches up on the next resize
    }
} ;

helpPanel.onClick = function(ev){
    const target = ev.target ;
    const action = target.closest("[data-help-action]") ;
    if(action){
        ev.preventDefault() ;
        this.onAction(action.getAttribute("data-help-action"), action) ;
        return ;
    }
    const ref = target.closest("[data-help-ref]") ;
    if(ref){
        ev.preventDefault() ;
        const value = ref.getAttribute("data-help-ref") ;
        //a section already displayed (ex: the summary at the top of a page) : just scroll to it
        if(!this.scrollToDisplayed(value)){
            this.show({mode: "read", ref: value}) ;
        }
        return ;
    }
    const screen = target.closest("[data-help-screen]") ;
    if(screen){
        ev.preventDefault() ;
        this.show({mode: "screen", code: screen.getAttribute("data-help-screen")}) ;
        return ;
    }
    const anchor = target.closest("a[href]") ;
    if(anchor && anchor.getAttribute("href").indexOf("#") === 0){
        ev.preventDefault() ;//never navigate in the application by accident
        return ;
    }
    const img = target.closest("img[data-help-img]") ;
    if(img){
        window.open(img.src, "_blank", "noopener") ;
    }
} ;

/**
 * @param {string} action data-help-action of the clicked element
 * @param {Element} [el] the clicked element (its data-help-turn / data-help-domain)
 */
helpPanel.onAction = function(action, el){
    if(action === "close"){ return this.close() ; }
    if(action === "back"){ return this.back() ; }
    if(action === "toc"){ return this.show({mode: "toc"}) ; }
    if(action === "screen"){ return this.show({mode: "screen"}) ; }
    if(action === "ask"){ return this.show({mode: "ask"}) ; }
    if(action === "ask-send"){ return this.submitAsk() ; }
    if(action === "ask-retry"){ return this.retryAsk() ; }
    if(action === "ask-new"){ return this.newConversation() ; }
    if(action === "ask-domain"){ return this.askDomain(turnOf(el), el ? el.getAttribute("data-help-domain") : null) ; }
    if(action.indexOf("feedback-") === 0){ return this.onFeedbackAction(action, turnOf(el)) ; }
} ;

/**
 * The exchange of the thread an element belongs to (data-help-turn), null if none
 */
function turnOf(el){
    const holder = el && el.closest ? el.closest("[data-help-turn]") : null ;
    const index = holder ? parseInt(holder.getAttribute("data-help-turn"), 10) : NaN ;
    return isNaN(index) ? null : index ;
}

helpPanel.back = function(){
    const previous = this.history.pop() ;
    if(previous){
        this.show(previous, false) ;
    }
} ;

helpPanel.onSearch = function(value){
    const query = String(value || "").trim() ;
    if(query){
        //typing refines the same search : one history entry for the whole search
        this.show({mode: "search", query: query}, !this.current || this.current.mode !== "search") ;
    }else if(this.current && this.current.mode === "search"){
        this.back() ;
    }
} ;

helpPanel.onRouteChanged = function(){
    if(!this.opened){ return ; }
    this.markToggle() ;
    const langChanged = this.lang && this.lang !== this.currentLang() ;
    if(!langChanged && this.current && this.current.mode === "ask"){
        //the question follows the screen silently : only its context line changes
        this.updateAskContext() ;
        return ;
    }
    if(langChanged || !this.current || this.current.mode === "screen"){
        //refs are language specific : a language switch goes back to the screen help
        this.history = [] ;
        this.show({mode: "screen"}, false) ;
    }else{
        this.host.classList.add("help-panel-screen-changed") ;
    }
} ;

/**
 * Translate the panel itself again after a language switch (the view translates its
 * data-i18n attributes when it is compiled only)
 */
helpPanel.translateChrome = function(){
    const elements = this.host.querySelectorAll("[data-i18n]") ;
    for(let i=0; i<elements.length; i++){
        const key = elements[i].getAttribute("data-i18n") ;
        const match = /^\[(.*)](.*)$/.exec(key) ;
        if(match){ elements[i].setAttribute(match[1], tr(match[2])) ; }
        else{ elements[i].textContent = tr(key) ; }
    }
} ;

helpPanel.setState = function(state){
    this.host.setAttribute("data-help-state", state) ;
} ;

/**
 * Display a target, keeping the previous one in the history
 *
 * @param {object} target {mode, ...}
 * @param {boolean} [push] keep the current target in the history (default true)
 */
helpPanel.show = async function(target, push){
    if(!this.opened){ return ; }
    const seq = ++this.renderSeq ;
    if(push !== false && this.current && !sameTarget(this.current, target)){
        this.history.push(this.current) ;
        if(this.history.length > HISTORY_MAX){ this.history.shift() ; }
    }
    this.current = target ;
    if(target.mode !== "search" && this.searchInput().value){
        this.searchInput().value = "" ;
    }
    if(target.mode === "screen"){
        this.host.classList.remove("help-panel-screen-changed") ;
    }
    this.host.classList.toggle("help-panel-has-history", this.history.length > 0) ;

    const lang = this.currentLang() ;
    if(lang !== this.lang){
        if(this.lang){ this.translateChrome() ; }
        this.lang = lang ;
    }

    const loadingTimer = setTimeout(() => { if(seq === this.renderSeq){ this.setState("loading") ; } }, 100) ;
    try{
        let index ;
        try{
            index = await this.loadIndex(lang) ;
        }catch(err){
            //no `npm run doc:help` in this build, desktop/mobile application, server down
            console.warn("Contextual help not available", err) ;
            if(seq === this.renderSeq){ this.setState("unavailable") ; }
            return ;
        }
        const rendered = await this.renderTarget(lang, index, target) ;
        if(seq !== this.renderSeq){ return ; }//something else was asked meanwhile
        helpTour.end() ;//its rows are about to be replaced
        this.contentEl().innerHTML = rendered.html ;
        this.setState(rendered.state) ;
        helpHighlight.render(index) ;
        this.scrollTo(rendered.anchor) ;
    }catch(err){
        console.error("Contextual help error", err) ;
        if(seq === this.renderSeq){ this.setState("unavailable") ; }
    }finally{
        clearTimeout(loadingTimer) ;
    }
} ;

function sameTarget(a, b){
    return a.mode === b.mode && a.ref === b.ref && a.code === b.code && a.query === b.query ;
}

/**
 * HTML of a target
 *
 * @return {Promise<{html, state, anchor}>}
 */
helpPanel.renderTarget = async function(lang, index, target){
    if(target.mode === "ask"){
        return this.renderAsk(index) ;
    }
    if(target.mode === "toc"){
        return {html: tocHtml(index, tr), state: "toc"} ;
    }
    if(target.mode === "search"){
        return {html: searchHtml(index, searchTitles(index, target.query, SEARCH_LIMIT), tr), state: "search"} ;
    }
    if(target.mode === "read"){
        const ref = parseRef(index, target.ref) ;
        if(!ref){
            return {html: '<p class="help-message">'+escapeHtml(tr("help.notFound"))+'</p>', state: "read"} ;
        }
        const pageData = await this.loadPage(lang, index, ref.page) ;
        return {html: readHtml(index, ref.page, pageData, tr), state: "read", anchor: ref.anchor} ;
    }
    //screen : the routes displayed, the explicitly chosen one (other open screens) on top
    let routeNames = this.appController.getActiveRouteNames() ;
    if(target.code){
        routeNames = routeNames.concat([target.code]) ;
    }
    const help = screenHelp(routeNames, index) ;
    if(!help.primary){
        return {html: "", state: "empty"} ;
    }
    const pagesData = new Map() ;
    await Promise.all(help.groups.map((g) => this.loadPage(lang, index, g.page).then((data) => pagesData.set(g.page, data)))) ;
    return {html: screenHtml(index, help, pagesData, tr), state: "screen"} ;
} ;

/**
 * Scroll to a reference when its target is in the displayed content
 *
 * @param {string} ref "page#anchor"
 * @return {boolean} true if found and scrolled to
 */
helpPanel.scrollToDisplayed = function(ref){
    const sharp = ref.indexOf("#") ;
    if(sharp === -1 || !this.current || (this.current.mode !== "screen" && this.current.mode !== "read")){ return false ; }
    const pageId = ref.substring(0, sharp) ;
    const anchor = ref.substring(sharp+1) ;
    const pages = this.contentEl().querySelectorAll("[data-help-page]") ;
    for(let i=0; i<pages.length; i++){
        if(pages[i].getAttribute("data-help-page") !== pageId){ continue ; }
        const el = findAnchor(pages[i], anchor) ;
        if(el){
            this.scrollToEl(el) ;
            return true ;
        }
    }
    return false ;
} ;

function findAnchor(root, anchor){
    const anchors = root.querySelectorAll("[data-help-anchor]") ;
    for(let i=0; i<anchors.length; i++){
        if(anchors[i].getAttribute("data-help-anchor") === anchor){ return anchors[i] ; }
    }
    return null ;
}

/**
 * Scroll to an anchor of the displayed page (opening the folded blocks around it), or to the top
 */
helpPanel.scrollTo = function(anchor){
    const el = anchor ? findAnchor(this.contentEl(), anchor) : null ;
    if(!el){
        this.host.querySelector("[data-help-body]").scrollTop = 0 ;
        return ;
    }
    this.scrollToEl(el) ;
} ;

/**
 * Scroll the panel body to an element, opening the folded blocks around it
 */
helpPanel.scrollToEl = function(el){
    const body = this.host.querySelector("[data-help-body]") ;
    let details = el.closest("details") ;
    while(details){
        details.open = true ;
        details = details.parentElement ? details.parentElement.closest("details") : null ;
    }
    body.scrollTop = el.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 8 ;
} ;

//──────────────────────────── "Ask a question" (L3.5) ────────────────────────────

/**
 * The conversation : turns = [{question, shown, domain, result, feedback}] where `shown` is what
 * the thread shows on the user's side (the domain label for a click on a clarification button,
 * the question otherwise) and `result` is null while pending or after a failure (`error`).
 * `seq` drops the answer of a call replaced meanwhile (new conversation).
 */
helpPanel.ask = {pending: false, error: null, seq: 0, index: null, conversationId: null, turns: []} ;
helpPanel.askStatus = null ;

helpPanel.askEl = function(selector){
    return this.host.querySelector(selector) ;
} ;

/**
 * The api client of the application, if the endpoints are there
 */
helpPanel.askApi = function(){
    const context = this.appController && this.appController.context ;
    const api = context && context.api ;
    return api && api.docAssistant && api.docAssistant.ask ? api.docAssistant : null ;
} ;

/**
 * Ask the server once whether the assistant is on (iz_ai_settings.usage_doc_assistant) : the
 * "Ask a question" entries are shown only then. A failure is retried at the next opening.
 */
helpPanel.loadAskStatus = function(){
    if(this.askStatus){ return this.askStatus ; }
    const api = this.askApi() ;
    if(!api){ return Promise.resolve(false) ; }
    const promise = new Promise((resolve) => {
        api.status((err, status) => {
            const enabled = !err && !!status && status.enabled === true ;
            this.host.classList.toggle("help-panel-ask-on", enabled) ;
            resolve(enabled) ;
        }) ;
    }) ;
    this.askStatus = promise ;
    promise.then((enabled) => {
        if(!enabled && this.askStatus === promise){ this.askStatus = null ; }//asked again at the next opening
    }) ;
    return promise ;
} ;

helpPanel.bindAsk = function(){
    const input = this.askEl("[data-help-ask-input]") ;
    input.addEventListener("keydown", (ev) => {
        //Enter sends, Shift+Enter goes to the next line
        if(ev.key === "Enter" && !ev.shiftKey && !ev.isComposing){
            ev.preventDefault() ;
            this.submitAsk() ;
        }
    }) ;
    //the comment of a thumbs down survives a re-rendering (reading a citation and coming back)
    this.askEl("[data-help-ask-result]").addEventListener("input", (ev) => {
        if(!ev.target.hasAttribute("data-help-feedback-comment")){ return ; }
        const turn = this.ask.turns[turnOf(ev.target)] ;
        if(turn){ turn.feedback.draft = ev.target.value ; }
    }) ;
} ;

/**
 * The screen given as context : the code carrying the help of the current screen, and the
 * title of its first documentation page
 */
helpPanel.askScreen = function(index){
    if(!index || !this.appController){ return null ; }
    const resolved = resolveScreens(this.appController.getActiveRouteNames(), index) ;
    if(!resolved.primary){ return null ; }
    const section = index.sections[resolved.primary.sections[0]] ;
    const page = section && index.pages[section.p] ;
    return {code: resolved.primary.code, title: page ? page.title : resolved.primary.code} ;
} ;

helpPanel.updateAskContext = function(){
    const el = this.askEl("[data-help-ask-context]") ;
    const screen = this.askScreen(this.ask.index) ;
    el.textContent = screen ? tr("help.askContext")+" "+screen.title : "" ;
    el.classList.toggle("d-none", !screen) ;
} ;

helpPanel.renderAsk = function(index){
    this.ask.index = index ;
    this.updateAskContext() ;
    this.renderAskResult() ;
    setTimeout(() => {
        if(this.opened && this.current && this.current.mode === "ask"){ this.askEl("[data-help-ask-input]").focus() ; }
    }, 0) ;
    return {html: "", state: "ask"} ;
} ;

/**
 * Exchanges allowed in the conversation : the value of the server when it gave one
 */
helpPanel.askMaxTurns = function(){
    const turns = this.ask.turns ;
    for(let i=turns.length-1; i>=0; i--){
        const max = turns[i].result && turns[i].result.maxTurns ;
        if(typeof max === "number" && max > 0){ return max ; }
    }
    return MAX_TURNS ;
} ;

/**
 * Exchanges done (answered, whatever the answer)
 */
helpPanel.askDone = function(){
    return this.ask.turns.filter((t) => t.result).length ;
} ;

helpPanel.renderAskResult = function(){
    const ask = this.ask ;
    const max = this.askMaxTurns() ;
    const done = this.askDone() ;
    const blocked = ask.pending || done >= max ;
    this.askEl("[data-help-ask-result]").innerHTML = threadHtml(ask.turns, {pending: ask.pending, error: ask.error, maxTurns: max}, tr) ;
    this.askEl("[data-help-ask-input]").disabled = blocked ;
    this.askEl("[data-help-ask-send]").disabled = blocked ;
    const started = ask.turns.length > 0 ;
    const count = this.askEl("[data-help-ask-count]") ;
    count.textContent = tr("help.askCount").replace("{n}", done).replace("{max}", max) ;
    count.classList.toggle("d-none", !started) ;
    this.askEl('[data-help-action="ask-new"]').classList.toggle("d-none", !started) ;
    this.host.classList.toggle("help-panel-ask-pending", ask.pending) ;
} ;

/**
 * Bring the last exchange into view (its question at the top of the panel body)
 */
helpPanel.scrollToLastTurn = function(){
    const last = this.ask.turns.length - 1 ;
    const el = last >= 0 ? this.askEl('[data-help-turn="'+last+'"]') : null ;
    if(el && el.getBoundingClientRect && this.current && this.current.mode === "ask"){ this.scrollToEl(el) ; }
} ;

/**
 * Random id grouping the journal rows of one conversation : 24 random bytes in base64url
 * (32 characters, the TOKEN format checked by the server)
 */
function newConversationId(){
    const bytes = new Uint8Array(24) ;
    const cryptoApi = typeof window !== "undefined" && window.crypto ? window.crypto : (typeof crypto !== "undefined" ? crypto : null) ;
    if(cryptoApi && cryptoApi.getRandomValues){
        cryptoApi.getRandomValues(bytes) ;
    }else{
        for(let i=0; i<bytes.length; i++){ bytes[i] = Math.floor(Math.random()*256) ; }
    }
    let binary = "" ;
    for(let i=0; i<bytes.length; i++){ binary += String.fromCharCode(bytes[i]) ; }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") ;
}

/**
 * Send a question : the one of the text area, or (opts) the original question of a clarification
 * again, restricted to the domain the user clicked
 *
 * @param {object} [opts] {question, domain, shown} for a click on a clarification button
 * @return {Promise} resolved once the answer (or the error) is displayed
 */
helpPanel.submitAsk = function(opts){
    const ask = this.ask ;
    const input = this.askEl("[data-help-ask-input]") ;
    const fromField = !opts || !opts.question ;
    const question = String(fromField ? input.value : opts.question).trim() ;
    const api = this.askApi() ;
    if(ask.pending){ return Promise.resolve() ; }
    //a failed exchange is replaced by the new question, it does not count
    //(an empty send keeps it : the question is gone from the field, the retry stays available)
    if(question){
        const lastTurn = ask.turns[ask.turns.length-1] ;
        if(lastTurn && !lastTurn.result){ ask.turns.pop() ; }
        ask.error = null ;
    }
    if(!question || !api || this.askDone() >= this.askMaxTurns()){
        this.renderAskResult() ;
        if(fromField && !input.disabled){ input.focus() ; }
        return Promise.resolve() ;
    }
    let uiLang = "fr" ;
    try{
        uiLang = WebView.i18n.getLang() || "fr" ;
    }catch(err){
        //i18n not configured yet : French, the default language of the application
    }
    const screen = this.askScreen(ask.index) ;
    if(!ask.conversationId){ ask.conversationId = newConversationId() ; }
    //the previous exchanges, text only : what the assistant answered (or asked back)
    const history = ask.turns.map((t) => ({
        question: t.question,
        answer: t.result.mode === "clarify" ? (t.result.clarify && t.result.clarify.question) || "" : (t.result.answer || ""),
        domain: t.result.domain || t.domain || null,
    })) ;
    const domain = !fromField && opts.domain ? opts.domain : null ;
    const turn = {question: question, shown: !fromField && opts.shown ? opts.shown : question, domain: domain, result: null, feedback: {}} ;
    ask.turns.push(turn) ;
    const seq = ++ask.seq ;
    ask.pending = true ;
    if(fromField){ input.value = "" ; }
    this.renderAskResult() ;
    this.scrollToLastTurn() ;
    return new Promise((resolve) => {
        api.ask(question, uiLang, screen ? screen.code : null, history, domain, ask.conversationId, (err, result) => {
            //a new conversation (or a newer question) was started meanwhile
            if(ask !== this.ask || seq !== ask.seq){ return resolve() ; }
            ask.pending = false ;
            if(err){ ask.error = err ; }
            else{ turn.result = result || {mode: "none", sections: []} ; }
            this.renderAskResult() ;
            this.scrollToLastTurn() ;
            if(!this.askEl("[data-help-ask-input]").disabled && this.current && this.current.mode === "ask"){
                this.askEl("[data-help-ask-input]").focus() ;
            }
            resolve() ;
        }) ;
    }) ;
} ;

/**
 * "Try again" after a failure : the failed exchange is sent again as it was
 */
helpPanel.retryAsk = function(){
    const ask = this.ask ;
    const failed = ask.turns[ask.turns.length-1] ;
    if(ask.pending || !failed || failed.result){ return this.submitAsk() ; }
    ask.turns.pop() ;
    return this.submitAsk({question: failed.question, domain: failed.domain, shown: failed.shown}) ;
} ;

/**
 * Click on a domain button of a clarification : the original question again, restricted to it.
 * Only on the last exchange, and only for a domain the assistant proposed.
 *
 * @param {number} turnIndex the exchange carrying the clarification
 * @param {string} domain the chosen domain key
 */
helpPanel.askDomain = function(turnIndex, domain){
    const turns = this.ask.turns ;
    const turn = turns[turnIndex] ;
    if(!turn || turnIndex !== turns.length-1 || !turn.result || turn.result.mode !== "clarify" || !turn.result.clarify){
        return Promise.resolve() ;
    }
    const option = (turn.result.clarify.options || []).find((o) => o.domain === domain) ;
    if(!option){ return Promise.resolve() ; }
    return this.submitAsk({question: turn.question, domain: option.domain, shown: option.label || option.domain}) ;
} ;

/**
 * Forget the conversation and start a new one ; an answer still on its way is dropped
 */
helpPanel.newConversation = function(){
    const ask = this.ask ;
    ask.seq++ ;
    ask.turns = [] ;
    ask.conversationId = null ;
    ask.pending = false ;
    ask.error = null ;
    this.renderAskResult() ;
    this.askEl("[data-help-ask-input]").focus() ;
    return Promise.resolve() ;
} ;

//──────────────────────────── Feedback on an answer (L3.6) ────────────────────────────

/**
 * The exchange a feedback action aims at : the given one, else the last one
 */
helpPanel.feedbackTurn = function(turnIndex){
    const turns = this.ask.turns ;
    return turns[turnIndex === null || turnIndex === undefined ? turns.length-1 : turnIndex] || null ;
} ;

/**
 * Thumbs up : sent at once. Thumbs down : opens the optional comment, sent with "Send".
 *
 * @param {string} action feedback-up | feedback-down | feedback-cancel | feedback-send
 * @param {number} [turnIndex] the exchange rated (data-help-turn), the last one by default
 */
helpPanel.onFeedbackAction = function(action, turnIndex){
    const turn = this.feedbackTurn(turnIndex) ;
    if(!turn || !turn.result){ return Promise.resolve() ; }
    const index = this.ask.turns.indexOf(turn) ;
    const feedback = turn.feedback ;
    const commentEl = () => this.askEl('[data-help-turn="'+index+'"] [data-help-feedback-comment]') ;
    if(feedback.sending || feedback.done){ return Promise.resolve() ; }
    if(action === "feedback-up"){ return this.sendFeedback("up", index) ; }
    if(action === "feedback-down"){
        feedback.open = true ;
        feedback.error = null ;
        this.renderAskResult() ;
        const comment = commentEl() ;
        if(comment){ comment.focus() ; }
        return Promise.resolve() ;
    }
    if(action === "feedback-cancel"){
        feedback.open = false ;
        this.renderAskResult() ;
        return Promise.resolve() ;
    }
    if(action === "feedback-send"){
        const comment = commentEl() ;
        if(comment){ feedback.draft = comment.value ; }
        return this.sendFeedback("down", index) ;
    }
    return Promise.resolve() ;
} ;

/**
 * Record the rating of an answer of the thread (api/docAssistant/feedback), once
 *
 * @param {string} rating "up" | "down"
 * @param {number} [turnIndex] the exchange rated, the last one by default
 * @return {Promise} resolved once the thank-you (or the error) is displayed
 */
helpPanel.sendFeedback = function(rating, turnIndex){
    const api = this.askApi() ;
    const turn = this.feedbackTurn(turnIndex) ;
    const result = turn && turn.result ;
    if(!api || !api.feedback || !result || !result.id || !result.feedbackToken){ return Promise.resolve() ; }
    const feedback = turn.feedback ;
    const comment = rating === "down" ? String(feedback.draft || "").trim() : "" ;
    feedback.sending = true ;
    feedback.rating = rating ;
    feedback.error = null ;
    this.renderAskResult() ;
    return new Promise((resolve) => {
        api.feedback(result.id, result.feedbackToken, rating, comment || null, (err) => {
            feedback.sending = false ;
            if(err){
                feedback.error = err ;
                feedback.rating = null ;
            }else{
                feedback.done = true ;
                feedback.open = false ;
            }
            //the exchange may have left the thread meanwhile (new conversation) : nothing to show
            if(this.ask.turns.indexOf(turn) !== -1){ this.renderAskResult() ; }
            resolve() ;
        }) ;
    }) ;
} ;

module.exports = helpPanel ;
